import React, { useState } from 'react';
import { format, parseISO, isBefore, addDays } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const MilestoneTimeline = ({ 
  milestones = [], 
  selectedMilestones = [], 
  onMilestoneSelect,
  onMilestoneEdit,
  onMilestoneDelete,
  onMilestoneComplete,
  onMilestoneReorder,
  isMobile = false
}) => {
  const [draggedMilestone, setDraggedMilestone] = useState(null);
  const [expandedMilestone, setExpandedMilestone] = useState(null);

  const getStatusConfig = (milestone) => {
    const today = new Date();
    const dueDate = parseISO(milestone?.dueDate);
    
    switch (milestone?.status) {
      case 'completed':
        return {
          color: 'success',
          bgColor: 'bg-success/10',
          textColor: 'text-success',
          borderColor: 'border-success',
          icon: 'CheckCircle',
          label: 'Completed'
        };
      case 'in-progress':
        return {
          color: 'accent',
          bgColor: 'bg-accent/10',
          textColor: 'text-accent',
          borderColor: 'border-accent',
          icon: 'Clock',
          label: 'In Progress'
        };
      case 'overdue':
        return {
          color: 'error',
          bgColor: 'bg-error/10',
          textColor: 'text-error',
          borderColor: 'border-error',
          icon: 'AlertTriangle',
          label: 'Overdue'
        };
      default:
        if (isBefore(dueDate, addDays(today, 7))) {
          return {
            color: 'warning',
            bgColor: 'bg-warning/10',
            textColor: 'text-warning',
            borderColor: 'border-warning',
            icon: 'AlertCircle',
            label: 'Due Soon'
          };
        }
        return {
          color: 'muted',
          bgColor: 'bg-muted',
          textColor: 'text-muted-foreground',
          borderColor: 'border-border',
          icon: 'Circle',
          label: 'Upcoming'
        };
    }
  };

  const getPriorityConfig = (priority) => {
    switch (priority) {
      case 'critical':
        return { color: 'text-error', bgColor: 'bg-error/10', label: 'Critical' };
      case 'high':
        return { color: 'text-warning', bgColor: 'bg-warning/10', label: 'High' };
      case 'medium':
        return { color: 'text-accent', bgColor: 'bg-accent/10', label: 'Medium' };
      default:
        return { color: 'text-muted-foreground', bgColor: 'bg-muted', label: 'Low' };
    }
  };

  const handleDragStart = (e, milestone) => {
    setDraggedMilestone(milestone);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetMilestone) => {
    e?.preventDefault();
    
    if (!draggedMilestone || draggedMilestone?.id === targetMilestone?.id) {
      return;
    }

    const reorderedMilestones = [...milestones];
    const draggedIndex = reorderedMilestones?.findIndex(m => m?.id === draggedMilestone?.id);
    const targetIndex = reorderedMilestones?.findIndex(m => m?.id === targetMilestone?.id);

    // Remove dragged milestone and insert at target position
    const [removed] = reorderedMilestones?.splice(draggedIndex, 1);
    reorderedMilestones?.splice(targetIndex, 0, removed);

    onMilestoneReorder?.(reorderedMilestones);
    setDraggedMilestone(null);
  };

  const handleMilestoneCheck = (milestoneId, checked) => {
    if (checked) {
      onMilestoneSelect?.([...selectedMilestones, milestoneId]);
    } else {
      onMilestoneSelect?.(selectedMilestones?.filter(id => id !== milestoneId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      onMilestoneSelect?.(milestones?.map(m => m?.id));
    } else {
      onMilestoneSelect?.([]);
    }
  };

  const sortedMilestones = [...milestones]?.sort((a, b) => 
    new Date(a.dueDate) - new Date(b.dueDate)
  );

  if (milestones?.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <Icon name="Calendar" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Milestones</h3>
          <p className="text-muted-foreground">
            Start by adding your first milestone to track contract progress.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Timeline Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Checkbox
            checked={selectedMilestones?.length === milestones?.length}
            indeterminate={selectedMilestones?.length > 0 && selectedMilestones?.length < milestones?.length}
            onCheckedChange={handleSelectAll}
          />
          <span className="text-sm text-muted-foreground">
            {selectedMilestones?.length > 0 
              ? `${selectedMilestones?.length} selected`
              : `${milestones?.length} milestones`
            }
          </span>
        </div>
        
        <div className="text-xs text-muted-foreground">
          Drag to reorder • Click to expand
        </div>
      </div>
      {/* Timeline */}
      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border"></div>

        {/* Milestones */}
        <div className="space-y-4">
          {sortedMilestones?.map((milestone, index) => {
            const statusConfig = getStatusConfig(milestone);
            const priorityConfig = getPriorityConfig(milestone?.priority);
            const isExpanded = expandedMilestone === milestone?.id;
            const isSelected = selectedMilestones?.includes(milestone?.id);

            return (
              <div
                key={milestone?.id}
                draggable
                onDragStart={(e) => handleDragStart(e, milestone)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, milestone)}
                className={`relative flex items-start space-x-4 p-4 rounded-lg border transition-legal-fast cursor-move hover:shadow-legal-md ${
                  isSelected 
                    ? 'border-primary bg-primary/5' 
                    : `border-border bg-card hover:border-${statusConfig?.color}`
                }`}
              >
                {/* Timeline Dot */}
                <div className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-2 ${statusConfig?.borderColor} ${statusConfig?.bgColor}`}>
                  <Icon name={statusConfig?.icon} size={16} className={statusConfig?.textColor} />
                </div>
                {/* Milestone Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3 mb-2">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={(checked) => handleMilestoneCheck(milestone?.id, checked)}
                    />
                    
                    <div className="flex-1">
                      <button
                        onClick={() => setExpandedMilestone(isExpanded ? null : milestone?.id)}
                        className="text-left w-full"
                      >
                        <h3 className="font-medium text-foreground hover:text-primary transition-legal-fast">
                          {milestone?.title}
                        </h3>
                      </button>
                    </div>

                    {/* Status Badge */}
                    <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig?.bgColor} ${statusConfig?.textColor}`}>
                      <Icon name={statusConfig?.icon} size={12} />
                      <span>{statusConfig?.label}</span>
                    </div>

                    {/* Priority Badge */}
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${priorityConfig?.bgColor} ${priorityConfig?.color}`}>
                      {priorityConfig?.label}
                    </div>
                  </div>

                  {/* Milestone Meta */}
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-2">
                    <div className="flex items-center space-x-1">
                      <Icon name="Calendar" size={14} />
                      <span>Due {format(parseISO(milestone?.dueDate), 'MMM dd, yyyy')}</span>
                    </div>
                    
                    <div className="flex items-center space-x-1">
                      <Icon name="User" size={14} />
                      <span>{milestone?.assignedParty}</span>
                    </div>

                    {milestone?.progress > 0 && (
                      <div className="flex items-center space-x-2">
                        <Icon name="BarChart3" size={14} />
                        <span>{milestone?.progress}%</span>
                        <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-accent transition-all duration-300"
                            style={{ width: `${milestone?.progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  {milestone?.description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {milestone?.description}
                    </p>
                  )}

                  {/* Dependencies */}
                  {milestone?.dependencies?.length > 0 && (
                    <div className="flex items-center space-x-2 mb-3">
                      <Icon name="GitBranch" size={14} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        Depends on: {milestone?.dependencies?.join(', ')}
                      </span>
                    </div>
                  )}

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-border space-y-3">
                      {milestone?.completedDate && (
                        <div className="flex items-center space-x-2 text-sm">
                          <Icon name="Check" size={14} className="text-success" />
                          <span className="text-muted-foreground">
                            Completed on {format(parseISO(milestone?.completedDate), 'MMM dd, yyyy')}
                          </span>
                        </div>
                      )}
                      
                      {milestone?.notificationEnabled && (
                        <div className="flex items-center space-x-2 text-sm">
                          <Icon name="Bell" size={14} className="text-accent" />
                          <span className="text-muted-foreground">
                            Reminder {milestone?.reminderDays} days before due date
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2 mt-3">
                    {milestone?.status !== 'completed' && (
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={() => onMilestoneComplete?.(milestone?.id)}
                        iconName="Check"
                      >
                        Mark Complete
                      </Button>
                    )}
                    
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => onMilestoneEdit?.(milestone)}
                      iconName="Edit3"
                    >
                      Edit
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => onMilestoneDelete?.(milestone?.id)}
                      iconName="Trash2"
                      className="text-error hover:text-error"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MilestoneTimeline;